import DIContainer from '@components/DependencyInjectionContainer.js';
import { getID } from '@components/Utility/GeneralUtil.js';
import { TOKEN } from '@components/Environment.js';
import { DateTimeManager } from '@components/DateTimeManager/DateTimeManager.js';
import { htmlToPlaintext, plaintextToHTML, escapeHtml } from '@components/Utility/StringUtil.js';
/**
 * Connects DOM elements to corresponding components
 * @param parentElement - The parent HTML element under which components will be searched for
 * @param components - An object with keys as component IDs and values as component instances
 */
export function connectDOM(parentElement, components) {
    // Query all elements with a data-componentid attribute
    const elements = parentElement.querySelectorAll('[data-componentid]');
    // Loop over the NodeList
    elements.forEach(element => {
        const componentID = element.getAttribute('data-componentid');
        if (components[componentID]) {
            components[componentID].rootElement = element;
            components[componentID].draw();
        }
    });
}
/**
 * Creates component instances from DOM elements.
 * For each DOM element with a 'data-componenttype' attribute, creates a new instance of the corresponding component type
 * and associates it with the DOM element. Component types are looked up in the global 's360' object.
 * @param parentElement - The parent HTML element under which component DOM elements will be searched for
 * @returns An object with keys as component IDs and values as component instances
 */
export function createComponentsFromDOM(parentElement) {
    const components = {};
    const s360 = window.s360;
    if (s360) {
        const elements = parentElement.querySelectorAll('[data-componenttype]');
        elements.forEach((element) => {
            const className = element.dataset.componenttype;
            if (className) {
                const id = element.dataset.componentid || getID(className);
                try {
                    const component = DIContainer.resolve(className, id, element);
                    component.rootElement = element;
                    components[id] = component;
                    component.draw();
                }
                catch (e) {
                    console.log(e);
                }
            }
        });
    }
    return components;
}
function parseConditionals(template, options, tokenValues) {
    if ((Reflect.has(options, 'enableConditionals') && !options.enableConditionals) || template.indexOf("{{#if") === -1) {
        return template;
    }
    const parseSingleCondition = (expr) => {
        const m = /^(!?)([^\s:=!]+)\s*(?::\s*([^\s=!:]+))?\s*(?:(!=|=)\s*(\$?)([^\s]+?))?\s*$/.exec(expr.trim());
        if (!m)
            return null;
        return {
            negated: m[1] === '!',
            tokenName: m[2],
            modifier: m[3],
            operator: m[4],
            compareWith: m[6],
            compareWithToken: m[5] === '$'
        };
    };
    const parseCondTag = (tag) => {
        const m = /^{{#(?:if|elseif)\s+(.*?)\s*}}$/.exec(tag.trim());
        if (!m)
            return null;
        const expr = m[1].trim();
        if (!expr)
            return null;
        const orParts = expr.split('||').map(s => s.trim()).filter(s => s);
        if (orParts.length === 0)
            return null;
        const orGroups = [];
        for (const orPart of orParts) {
            const andParts = orPart.split('&&').map(s => s.trim()).filter(s => s);
            if (andParts.length === 0)
                return null;
            const andGroup = [];
            for (const part of andParts) {
                const cond = parseSingleCondition(part);
                if (!cond)
                    return null;
                andGroup.push(cond);
            }
            orGroups.push(andGroup);
        }
        return { orGroups };
    };
    const conditionsManager = DIContainer.getInstance('ConditionsManager');
    const resolveTokenValue = (tokenName, modifier) => {
        let tokenValue = '';
        if (tokenValues && Reflect.has(tokenValues, tokenName)) {
            tokenValue = tokenValues[tokenName];
        }
        else if (options.records && tokenName.indexOf('.') > -1) {
            const dotIndex = tokenName.indexOf('.');
            const recordName = tokenName.substring(0, dotIndex);
            const fieldName = tokenName.substring(dotIndex + 1);
            const record = options.records[recordName];
            if (record && Reflect.has(record.data, fieldName)) {
                tokenValue = record.data[fieldName] || '';
            }
            else if (options.parseTokenCallback) {
                tokenValue = options.parseTokenCallback(tokenName, modifier || '') ?? '';
            }
        }
        else if (options.parseTokenCallback) {
            tokenValue = options.parseTokenCallback(tokenName, modifier || '') ?? '';
        }
        if (modifier)
            tokenValue = formatToken(tokenValue, modifier);
        return tokenValue;
    };
    const evalSingleCondition = (cond) => {
        let result;
        if (cond.tokenName === 'perm') {
            result = cond.modifier ? conditionsManager.checkPerm(cond.modifier) : false;
        }
        else {
            const tokenValue = resolveTokenValue(cond.tokenName, cond.modifier);
            if (cond.compareWith !== undefined && cond.compareWith !== null) {
                if (cond.compareWithToken) {
                    const otherValue = resolveTokenValue(cond.compareWith);
                    const match = tokenValue === otherValue;
                    result = cond.operator === '!=' ? !match : match;
                }
                else {
                    const values = cond.compareWith.split(',');
                    const match = values.includes(tokenValue);
                    result = cond.operator === '!=' ? !match : match;
                }
            }
            else {
                result = tokenValue !== '' && tokenValue !== null && tokenValue !== undefined;
            }
        }
        return cond.negated ? !result : result;
    };
    const evalCompoundCondition = (compound) => {
        return compound.orGroups.some(andGroup => andGroup.every(cond => evalSingleCondition(cond)));
    };
    const OPEN_IF = "{{#if";
    const OPEN_ELSEIF = "{{#elseif";
    const ELSE = "{{#else}}";
    const CLOSE = "{{/if}}";
    // Reduce innermost blocks repeatedly
    let firstOpen = template.indexOf(OPEN_IF);
    while (firstOpen > -1) {
        firstOpen = template.indexOf(OPEN_IF);
        if (firstOpen === -1)
            break;
        // Find innermost {{#if ...}} ... {{/if}} using a stack
        let i = 0;
        const stack = [];
        let foundPair = null;
        while (i < template.length) {
            const nextOpen = template.indexOf(OPEN_IF, i);
            const nextClose = template.indexOf(CLOSE, i);
            if (nextOpen !== -1 && (nextOpen < nextClose || nextClose === -1)) {
                const tagEnd = template.indexOf("}}", nextOpen);
                if (tagEnd === -1)
                    break; // malformed
                stack.push({ openStart: nextOpen, openEnd: tagEnd + 2 });
                i = tagEnd + 2;
            }
            else if (nextClose !== -1) {
                const opener = stack.pop();
                if (!opener) {
                    // Unbalanced; skip this close and continue
                    i = nextClose + CLOSE.length;
                    continue;
                }
                foundPair = {
                    openStart: opener.openStart,
                    openEnd: opener.openEnd,
                    closeStart: nextClose,
                    closeEnd: nextClose + CLOSE.length
                };
                break; // innermost found
            }
            else {
                break;
            }
        }
        if (!foundPair)
            break;
        const { openStart, openEnd, closeStart, closeEnd } = foundPair;
        // Parse head {{#if ...}}
        const openTag = template.slice(openStart, openEnd);
        const head = parseCondTag(openTag);
        if (!head) {
            // malformed opener; drop the tag to avoid infinite loop
            template = template.slice(0, openStart) + template.slice(openEnd);
            continue;
        }
        // Inner content of this block (between head and close)
        const inner = template.slice(openEnd, closeStart);
        const segments = [{ cond: head, content: "" }];
        let elseContent = "";
        let cursor = 0;
        let hitElse = false;
        const findNextMarker = (from) => {
            const idxElseIf = inner.indexOf(OPEN_ELSEIF, from);
            const idxElse = inner.indexOf(ELSE, from);
            if (idxElseIf === -1 && idxElse === -1)
                return { type: null, idx: -1 };
            if (idxElseIf !== -1 && (idxElseIf < idxElse || idxElse === -1)) {
                return { type: "elseif", idx: idxElseIf };
            }
            return { type: "else", idx: idxElse };
        };
        while (cursor <= inner.length) {
            const { type, idx } = findNextMarker(cursor);
            if (!type) {
                // No more markers
                if (hitElse)
                    elseContent += inner.slice(cursor);
                else
                    segments[segments.length - 1].content += inner.slice(cursor);
                break;
            }
            if (type === "elseif") {
                // Content up to elseif belongs to current segment
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                // Read elseif tag
                const tagEnd = inner.indexOf("}}", idx);
                if (tagEnd === -1)
                    break; // malformed
                const elseifTag = inner.slice(idx, tagEnd + 2);
                const parsedElseIf = parseCondTag(elseifTag);
                segments.push({ cond: parsedElseIf ?? undefined, content: "" });
                cursor = tagEnd + 2;
            }
            else {
                // ELSE
                segments[segments.length - 1].content += inner.slice(cursor, idx);
                cursor = idx + ELSE.length;
                hitElse = true;
                elseContent = inner.slice(cursor); // rest belongs to else
                break;
            }
        }
        // Evaluate segments in order (first truthy wins), otherwise else (if present)
        let replacement = "";
        const winner = segments.find(s => s.cond && evalCompoundCondition(s.cond));
        if (winner)
            replacement = winner.content;
        else if (hitElse)
            replacement = elseContent;
        // Replace whole block
        template = template.slice(0, openStart) + replacement + template.slice(closeEnd);
    }
    return template;
}
function parseModifierChain(modifierChain) {
    if (!modifierChain)
        return [];
    return modifierChain.split('|').map(segment => {
        const match = /^([^(]+)(?:\(([^)]*)\))?$/.exec(segment.trim());
        if (!match)
            return { name: segment.trim(), params: [] };
        return {
            name: match[1].trim(),
            params: match[2] ? match[2].split(',').map(p => p.trim()) : []
        };
    });
}
function applyModifiers(tokenValue, modifiers) {
    for (const mod of modifiers) {
        tokenValue = formatToken(tokenValue, mod.name, mod.params);
    }
    return tokenValue;
}
function resolveToken(tokenName, modifierChain, tokenValues, options = {}) {
    let tokenValue;
    if (!options.recordTokensOnly && tokenValues && (Reflect.has(tokenValues, tokenName) || Reflect.has(tokenValues, tokenName.toLowerCase()))) {
        tokenValue = tokenValues[tokenName] || tokenValues[tokenName.toLowerCase()] || '';
    }
    else if (options.records && tokenName.indexOf('.') > -1) {
        /* we can add this eventually if we decide we want to support nested properties
                and just remove the "options.records" portion of the above if statement
                if (tokenValues) {
                    const deep = getDeepProperty(tokenValues, tokenNamePart);
                    if (deep !== undefined) tokenValue = String(deep);
                }*/
        const dotIndex = tokenName.indexOf('.');
        const recordName = tokenName.substring(0, dotIndex);
        const fieldName = tokenName.substring(dotIndex + 1);
        const record = options.records[recordName];
        if (record && Reflect.has(record.data, fieldName)) {
            tokenValue = record.data[fieldName] || '';
        }
    }
    if (tokenName === 'NOW') {
        tokenValue = DateTimeManager.now('SYSTEM', 'string');
    }
    else if (tokenName.startsWith('NOW_')) {
        tokenValue = DateTimeManager.now(tokenName.substring(4), 'string');
    }
    if (!options.recordTokensOnly && typeof tokenValue === 'undefined') {
        tokenValue = TOKEN(`{{${tokenName}}}`);
    }
    if (typeof tokenValue === 'undefined' && options.parseTokenCallback) {
        tokenValue = options.parseTokenCallback(tokenName, modifierChain || '');
    }
    if (typeof tokenValue === 'undefined' && !options.keepMissingTokens) {
        tokenValue = '';
    }
    if (tokenValue !== undefined && modifierChain) {
        const modifiers = parseModifierChain(modifierChain);
        if (modifiers.length > 0) {
            if (modifiers[0].name === 'tokenLiteral' || modifiers[0].name === 'caption') {
                tokenValue = tokenName;
            }
            tokenValue = applyModifiers(tokenValue, modifiers);
        }
    }
    return tokenValue;
}
export function replaceTokens(template, tokenValues, options = {}) {
    template = parseConditionals(template, options, tokenValues);
    // Check if there are any potential global tokens before using regex
    let hasComponents = false;
    if (template.indexOf('{{') > -1) {
        const tokenRegex = /{{([^}]+)}}/g;
        template = template.replace(tokenRegex, (match, tokenTag) => {
            if (tokenTag.charCodeAt(0) === 64) {
                hasComponents = true;
                return match;
            }
            const colonIndex = tokenTag.indexOf(':');
            const tokenNamePart = colonIndex > -1 ? tokenTag.substring(0, colonIndex) : tokenTag;
            const modifierChain = colonIndex > -1 ? tokenTag.substring(colonIndex + 1) : '';
            let tokenValue = resolveToken(tokenNamePart, modifierChain, tokenValues, options);
            if (tokenValue !== undefined && options.urlEncode && !parseModifierChain(modifierChain).some(m => m.name === 'encoded')) {
                tokenValue = encodeURIComponent(tokenValue);
            }
            return tokenValue !== undefined ? tokenValue : match;
        });
    }
    if (hasComponents) {
        template = expandComponents(template, (tokenName, modifierChain) => {
            return resolveToken(tokenName, modifierChain, tokenValues, options) ?? '';
        });
    }
    return template;
}
const componentRegistry = {
    button: (attrs) => {
        const id = escapeHtml(attrs.id || '');
        const icon = escapeHtml(attrs.icon || '');
        const caption = escapeHtml(attrs.caption || '');
        const hint = attrs.hint ? escapeHtml(attrs.hint) : '';
        let html = `<button data-button-id="${id}">`
            + `<i class="material-symbols-outlined button-icon">${icon}</i>`
            + '<div class="button-text-container">'
            + `<span class="button-text">${caption}</span>`;
        if (hint)
            html += `<div class="button-hint">${hint}</div>`;
        html += '</div></button>';
        return html;
    },
    icon: (attrs) => {
        const name = escapeHtml(attrs.icon || '');
        const sizeClass = attrs.size ? ` icon-${escapeHtml(attrs.size)}` : '';
        const colorClass = attrs.color ? ` text-color-${escapeHtml(attrs.color)}` : '';
        return `<i class="material-symbols-outlined${sizeClass}${colorClass}">${name}</i>`;
    },
    badge: (attrs) => {
        const icon = attrs.icon ? escapeHtml(attrs.icon) : '';
        const caption = attrs.caption ? escapeHtml(attrs.caption) : '';
        const color = attrs.color ? escapeHtml(attrs.color).toLowerCase() : '';
        const colorClass = color ? ` color-${color}` : '';
        const iconClass = icon && !caption ? ' badge-icon' : '';
        const circleClass = attrs.circle ? ' circle-badge' : '';
        let html = `<span class="badge${colorClass}${iconClass}${circleClass}">`;
        if (icon)
            html += `<i class="material-symbols-outlined icon-${circleClass && iconClass ? 'md' : 'sm'}">${icon}</i>`;
        if (caption)
            html += caption;
        html += '</span>';
        return html;
    }
};
function resolveQuotedValue(value, resolveToken) {
    if (value.indexOf('{{') === -1)
        return value;
    return value.replace(/{{([^}]+)}}/g, (_m, tokenTag) => {
        const colonIdx = tokenTag.indexOf(':');
        const tokenName = colonIdx > -1 ? tokenTag.substring(0, colonIdx) : tokenTag;
        const modifier = colonIdx > -1 ? tokenTag.substring(colonIdx + 1) : '';
        return resolveToken(tokenName, modifier);
    });
}
function parseComponentAttrs(attrString, resolveToken) {
    const attrs = {};
    const regex = /(\w+)(?:=(?:"([^"]*)"|(\S+)))?/g;
    let m;
    while ((m = regex.exec(attrString)) !== null) {
        if (m[2] !== undefined) {
            attrs[m[1]] = resolveQuotedValue(m[2], resolveToken);
        }
        else if (m[3] !== undefined) {
            if (m[3].charAt(0) === '$') {
                const tokenRef = m[3].substring(1);
                const colonIdx = tokenRef.indexOf(':');
                const tokenName = colonIdx > -1 ? tokenRef.substring(0, colonIdx) : tokenRef;
                const modifier = colonIdx > -1 ? tokenRef.substring(colonIdx + 1) : '';
                attrs[m[1]] = resolveToken(tokenName, modifier);
            }
            else {
                attrs[m[1]] = m[3];
            }
        }
        else {
            attrs[m[1]] = 'true';
        }
    }
    return attrs;
}
function expandComponents(template, resolveToken) {
    let result = '';
    let i = 0;
    while (i < template.length) {
        const start = template.indexOf('{{@', i);
        if (start === -1) {
            result += template.slice(i);
            break;
        }
        result += template.slice(i, start);
        let depth = 0;
        let j = start;
        let end = -1;
        while (j < template.length - 1) {
            if (template[j] === '{' && template[j + 1] === '{') {
                depth++;
                j += 2;
            }
            else if (template[j] === '}' && template[j + 1] === '}') {
                depth--;
                if (depth === 0) {
                    end = j + 2;
                    break;
                }
                j += 2;
            }
            else {
                j++;
            }
        }
        if (end === -1) {
            result += template.slice(start);
            break;
        }
        const inner = template.slice(start + 3, end - 2);
        const spaceIdx = inner.indexOf(' ');
        if (spaceIdx === -1) {
            result += template.slice(start, end);
            i = end;
            continue;
        }
        const name = inner.substring(0, spaceIdx);
        const attrString = inner.substring(spaceIdx + 1);
        const renderer = componentRegistry[name];
        if (!renderer) {
            result += template.slice(start, end);
        }
        else {
            result += renderer(parseComponentAttrs(attrString, resolveToken));
        }
        i = end;
    }
    return result;
}
export function registerComponent(name, renderer) {
    componentRegistry[name] = renderer;
}
export function extractTemplateTokens(template) {
    const results = [];
    const seen = new Set();
    const add = (raw) => {
        const colonIdx = raw.indexOf(':');
        const name = colonIdx > -1 ? raw.substring(0, colonIdx) : raw;
        const modifier = colonIdx > -1 ? raw.substring(colonIdx + 1) : '';
        const key = `${name}:${modifier}`;
        if (name && !seen.has(key)) {
            seen.add(key);
            results.push({ name, modifier });
        }
    };
    const tokenRegex = /{{([^}]+)}}/g;
    const condTokenRegex = /^!?([^\s:=!]+)(?::([^\s=!:]+))?/;
    let match;
    while ((match = tokenRegex.exec(template)) !== null) {
        const token = match[1];
        if (token.charAt(0) === '@') {
            const embeddedRegex = /{{([\w.]+(?::[^}]+)?)/g;
            let em;
            while ((em = embeddedRegex.exec(token)) !== null) {
                add(em[1]);
            }
            continue;
        }
        if (token === '#else' || token.startsWith('/if'))
            continue;
        if (token.startsWith('#if ') || token.startsWith('#elseif ')) {
            const expr = token.replace(/^#(?:else)?if\s+/, '');
            for (const orPart of expr.split('||')) {
                for (const andPart of orPart.split('&&')) {
                    const m = condTokenRegex.exec(andPart.trim());
                    if (m)
                        add(m[2] ? `${m[1]}:${m[2]}` : m[1]);
                }
            }
            continue;
        }
        add(token);
    }
    const componentTokenRegex = /\$([\w.]+(?::\w+)?)/g;
    while ((match = componentTokenRegex.exec(template)) !== null) {
        add(match[1]);
    }
    return results;
}
export function formatToken(tokenValue, modifier, params = []) {
    if (tokenValue) {
        switch (modifier) {
            case 'dateFormat':
                tokenValue = DateTimeManager.formatDate(tokenValue, 'display');
                break;
            case 'datetimeFormat':
                tokenValue = DateTimeManager.formatDateTime(tokenValue, 'display');
                break;
            case 'plaintext':
                tokenValue = htmlToPlaintext(tokenValue);
                break;
            case 'html':
                tokenValue = plaintextToHTML(tokenValue);
                break;
            case 'tokenLiteral':
                tokenValue = `{{${tokenValue}}}`;
                break;
            case 'tokenEncoded':
                tokenValue = encodeURIComponent(tokenValue);
                break;
            case 'round0':
                tokenValue = tokenValue ? Number(tokenValue).toFixed(0) : tokenValue;
                break;
            case 'toLower':
                tokenValue = String(tokenValue).toLowerCase();
                break;
            case 'toUpper':
                tokenValue = String(tokenValue).toUpperCase();
                break;
            case 'caption':
                tokenValue = DIContainer.getInstance('CaptionManager').getCaption(tokenValue);
                break;
            case 'dateAdd': {
                const amount = parseFloat(params[0] || '0');
                const unit = (params[1] || 'days');
                if (!isNaN(amount))
                    tokenValue = DateTimeManager.dateAdd(tokenValue, unit, amount);
                break;
            }
            case 'round': {
                const decimals = parseInt(params[0] || '0', 10);
                if (!isNaN(decimals))
                    tokenValue = Number(tokenValue).toFixed(decimals);
                break;
            }
        }
    }
    switch (modifier) {
        case 'ifNull':
            if (tokenValue === null || tokenValue === undefined)
                tokenValue = params[0] || '';
            break;
        case 'ifEmpty':
            if (!tokenValue)
                tokenValue = params[0] || '';
            break;
        case 'ifTmpKey':
            if (tokenValue && tokenValue.startsWith('tmp_'))
                tokenValue = params[0] || '';
            break;
        case 'stepUp': {
            const value = parseFloat(tokenValue) || 0;
            const increment = parseFloat(params[0] || '1');
            if (isNaN(increment) || increment === 0)
                break;
            const stepsUp = Math.floor(value / increment);
            let result = (stepsUp + 1) * increment;
            if (params[1] !== undefined && params[1] !== '')
                result = Math.min(result, parseFloat(params[1]));
            tokenValue = String(parseFloat(result.toFixed(10)));
            break;
        }
        case 'stepDown': {
            const value = parseFloat(tokenValue) || 0;
            const increment = parseFloat(params[0] || '1');
            if (isNaN(increment) || increment === 0)
                break;
            const stepsDown = Math.ceil(value / increment);
            let result = (stepsDown - 1) * increment;
            if (params[1] !== undefined && params[1] !== '')
                result = Math.max(result, parseFloat(params[1]));
            tokenValue = String(parseFloat(result.toFixed(10)));
            break;
        }
    }
    return tokenValue;
}
export function createElement(tag, classNames = [], attributes = {}) {
    const element = document.createElement(tag);
    element.classList.add(...classNames);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);
    }
    return element;
}
